// Configuración → API para IAs → "Conectar ChatGPT": servidor OAuth 2.1 del ERP para el conector MCP.
import { useCallback, useEffect, useState } from 'react';
import api from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Skeleton } from '../../../components/ui/skeleton';
import { Badge } from '../../../components/ui/badge';
import { MessageSquareCode, Globe, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CopiaRow } from './oauthUtils';
import { OAuthClientes, SecretoAlert } from './OAuthClientes';
import { OAuthConexiones } from './OAuthConexiones';
import { OAuthAccesos } from './OAuthAccesos';

function Diagnostico({ cfg }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const urls = [
    ['Relativo a la URL del MCP: /api/mcp/.well-known/oauth-protected-resource (lo que pide ChatGPT)', `${cfg.mcp_url}/.well-known/oauth-protected-resource`],
    ['Relativo a la URL del MCP: /api/mcp/.well-known/oauth-authorization-server', `${cfg.mcp_url}/.well-known/oauth-authorization-server`],
    ['Metadata del servidor OAuth ({issuer}/.well-known/oauth-authorization-server)', cfg.metadata_url],
    ['Metadata del recurso (RFC 9728, la del header WWW-Authenticate)', cfg.resource_metadata_url],
    ['Raíz del dominio: /.well-known/oauth-protected-resource/api/mcp (archivo estático)', cfg.resource_metadata_root_url],
    ['Raíz del dominio: /.well-known/oauth-authorization-server/api/oauth (archivo estático)', `${cfg.url_publica}/.well-known/oauth-authorization-server/api/oauth`],
    ['Servidor MCP responde 401 + WWW-Authenticate', cfg.mcp_url],
  ];
  const correr = async () => {
    setBusy(true);
    const out = [];
    for (const [label, url] of urls) {
      try {
        const esMcp = url === cfg.mcp_url;
        const r = await fetch(url, esMcp ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'tools/list' }) } : { redirect: 'follow' });
        const j = esMcp ? null : await r.json().catch(() => ({}));
        const ok = esMcp ? r.status === 401 && /resource_metadata=/.test(r.headers.get('www-authenticate') || '') : r.ok && (j.issuer || j.resource);
        out.push({ label, url, ok: !!ok, status: r.status + (r.redirected ? ' vía redirección' : '') });
      } catch { out.push({ label, url, ok: false, status: 'sin respuesta' }); }
    }
    setRes(out); setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="oauth-diagnostico">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Verificar descubrimiento OAuth</p>
        <Button size="sm" variant="outline" onClick={correr} disabled={busy} data-testid="oauth-btn-diagnostico">{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Globe className="mr-1 h-3.5 w-3.5" />} Verificar</Button>
      </div>
      {res && res.map((r) => (
        <div key={r.url} className="flex items-center gap-2 text-xs" data-testid={`oauth-diag-${r.ok ? 'ok' : 'fail'}`}>
          {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-600" />}
          <span className="font-medium">{r.label}</span><span className="truncate font-mono text-muted-foreground">{r.url}</span><span className="text-muted-foreground">({r.status})</span>
        </div>
      ))}
    </div>
  );
}

export default function ConectorChatGPT() {
  const [cfg, setCfg] = useState(null);
  const [conexiones, setConexiones] = useState([]);
  const [secreto, setSecreto] = useState(null);
  const [mostrarPasos, setMostrarPasos] = useState(false);

  const cargarCfg = useCallback(async () => {
    try { const r = await api.get('/oauth/admin/config'); setCfg(r.data); } catch (e) { toast.error(e.response?.data?.detail || 'Error cargando configuración OAuth'); }
  }, []);
  const cargarConexiones = useCallback(async (incluirRevocadas = false) => {
    try { const r = await api.get('/oauth/admin/conexiones', { params: { incluir_revocadas: incluirRevocadas } }); setConexiones(r.data.conexiones || []); } catch (e) { toast.error(e.response?.data?.detail || 'Error cargando conexiones'); }
  }, []);
  useEffect(() => { cargarCfg(); cargarConexiones(); }, [cargarCfg, cargarConexiones]);

  const guardarDcr = async (v) => {
    try { await api.put('/oauth/admin/config', { dcr_habilitado: v }); toast.success(v ? 'Registro dinámico habilitado (solo chatgpt.com)' : 'Registro dinámico deshabilitado'); cargarCfg(); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  if (!cfg) return <Card data-testid="oauth-card-loading"><CardContent className="py-6"><Skeleton className="h-24 w-full" /></CardContent></Card>;

  return (
    <Card data-testid="oauth-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><MessageSquareCode className="h-4 w-4" /> Conectar ChatGPT (OAuth 2.1)</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">ChatGPT ya no acepta API keys en conectores MCP: cada usuario autoriza con su cuenta del ERP y las acciones se ejecutan con su identidad, rol y permisos.</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Badge variant="outline" data-testid="oauth-badge-clientes">{cfg.clientes.filter((c) => c.activo).length} clientes</Badge>
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="oauth-badge-conexiones">{cfg.total_conexiones_activas} conexiones activas</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <SecretoAlert secreto={secreto} onCerrar={() => setSecreto(null)} />

        <div className="grid gap-2 md:grid-cols-2">
          <CopiaRow label="URL del servidor MCP (pégala en ChatGPT)" valor={cfg.mcp_url} testid="oauth-url-mcp" />
          <CopiaRow label="Metadata OAuth (descubrimiento automático)" valor={cfg.metadata_root_url} testid="oauth-url-metadata" />
          <CopiaRow label="Authorize" valor={cfg.authorize_url} testid="oauth-url-authorize" />
          <CopiaRow label="Token" valor={cfg.token_url} testid="oauth-url-token" />
        </div>

        <div className="rounded-xl border p-3" data-testid="oauth-manual">
          <p className="text-sm font-semibold">Si ChatGPT pide “introducir los ajustes manualmente”</p>
          <p className="mb-2 text-xs text-muted-foreground">Copia cada valor tal cual en el campo correspondiente del formulario del conector.</p>
          <div className="grid gap-2 md:grid-cols-2">
            {cfg.manual.map((m) => <CopiaRow key={m.campo} label={m.campo} valor={m.valor} testid={`oauth-manual-${m.campo.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} mono={!m.valor.startsWith('(')} />)}
          </div>
        </div>

        <OAuthClientes clientes={cfg.clientes} onReload={cargarCfg} onSecreto={setSecreto} />
        <OAuthConexiones conexiones={conexiones} onReload={cargarConexiones} />
        <div className="rounded-xl border p-3"><OAuthAccesos /></div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">Registro dinámico de clientes (DCR)</p>
                <p className="text-xs text-muted-foreground">Permite que ChatGPT registre su propio cliente sin pegar Client ID/Secret. Restringido a URLs de retorno de chatgpt.com.</p>
              </div>
              <Switch checked={cfg.dcr_habilitado} onCheckedChange={guardarDcr} data-testid="oauth-switch-dcr" />
            </div>
          </div>
          <div className="rounded-xl border p-3"><Diagnostico cfg={cfg} /></div>
        </div>

        <div>
          <button type="button" onClick={() => setMostrarPasos((v) => !v)} className="text-sm font-semibold text-indigo-700 hover:underline" data-testid="oauth-toggle-pasos">
            {mostrarPasos ? 'Ocultar' : 'Ver'} pasos para configurar el conector en ChatGPT
          </button>
          {mostrarPasos && (
            <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground" data-testid="oauth-pasos">
              {cfg.pasos.map((p) => <li key={p}>{p}</li>)}
            </ol>
          )}
          <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            URLs de retorno oficiales de ChatGPT aceptadas sin configurar: {cfg.redirects_chatgpt.join(' · ')}. Si el conector muestra otra, agrégala al cliente con “URIs”.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
